import { useMemo } from 'react';
import { Product } from '../../types/product';

interface ProductStats {
  totalProducts: number;
  averagePrice: string;
  productsWithOptions: number;
  uniqueCategories: number;
  validPrices: number[];
  hasValidPrices: boolean;
  priceRange: {
    min: number;
    max: number;
  };
  categoryDistribution: Record<string, number>;
}

export const useProductStats = (products: Product[]): ProductStats => {
  return useMemo(() => {
    const totalProducts = products.length;

    // Price calculations
    const validPrices = products
      .map(product => {
        const price = typeof product.price === 'string' ? parseFloat(product.price) : product.price;
        return isNaN(price) ? 0 : price;
      })
      .filter(price => price > 0);

    const averagePrice =
      validPrices.length > 0
        ? (validPrices.reduce((sum, price) => sum + price, 0) / validPrices.length).toFixed(2)
        : '0.00';

    // Price range
    const priceRange =
      validPrices.length > 0
        ? {
            min: Math.min(...validPrices),
            max: Math.max(...validPrices),
          }
        : { min: 0, max: 0 };

    // Other statistics
    const productsWithOptions = products.filter(p => p.has_options).length;
    const uniqueCategories = new Set(products.map(p => p.main_category_id)).size;

    // Category distribution
    const categoryDistribution = products.reduce(
      (acc, product) => {
        const categoryId = product.main_category_id?.toString() || 'unknown';
        acc[categoryId] = (acc[categoryId] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );

    return {
      totalProducts,
      averagePrice,
      productsWithOptions,
      uniqueCategories,
      validPrices,
      hasValidPrices: validPrices.length > 0,
      priceRange,
      categoryDistribution,
    };
  }, [products]);
};
